import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, Hammer, Cpu, Truck, Sparkles, MessageSquare, Users2, Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";

export default function Landing() {
  const navigate = useNavigate();
  const { currentUser, theme, toggleTheme } = useApp();

  if (currentUser) {
    navigate(currentUser.role === "employer" ? "/employer" : "/worker", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen grain">
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-sm">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <span className="font-heading font-extrabold text-xl tracking-tight">SkillBridge</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="landing-theme-toggle" onClick={toggleTheme} className="text-xs text-muted-foreground hover:text-foreground">
            {theme === "dark" ? "☀ Light" : "☾ Dark"}
          </button>
          <Button data-testid="landing-signin-btn" variant="ghost" size="sm" onClick={() => navigate("/login")}>Sign in</Button>
          <Button data-testid="landing-signup-btn" size="sm" onClick={() => navigate("/register")}>Get started</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-primary uppercase tracking-[0.12em] mb-5">
            <Sparkles className="w-3.5 h-3.5" /> AI-verified · Connect · Message · Recommend
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-foreground"
          >
            Real skills, <span className="text-primary">verified</span> — and a network to prove it.
          </motion.h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/75 max-w-xl">
            SkillBridge turns hands-on experience into trusted digital credentials, connects workers with employers, and lets both sides message, endorse, and recommend each other — the trust layer for informal work.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button data-testid="hero-signup-btn" size="lg" onClick={() => navigate("/register")}>
              Create free account <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="hero-signin-btn" size="lg" variant="outline" onClick={() => navigate("/login")}>
              Sign in
            </Button>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-3">
            <Feature icon={<Award className="w-4 h-4" />} title="Verified credentials" text="AI interviews turn experience into portable badges" />
            <Feature icon={<Users2 className="w-4 h-4" />} title="Real network" text="Connect with employers and peers — request, accept, message" />
            <Feature icon={<MessageSquare className="w-4 h-4" />} title="Recommendations" text="Ask connections to write recommendations you can showcase" />
          </div>
        </div>

        <div className="hidden lg:block">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                      className="rounded-xl border border-border bg-card p-6 shadow-xl shadow-primary/5">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Live proof</div>
            <div className="mt-2 font-heading text-xl font-extrabold">Miguel Torres</div>
            <div className="text-sm text-muted-foreground">Master Carpenter · Nairobi</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Wood Joinery","Cabinet Making","Furniture Assembly","Blueprint Reading"].map((s) => (
                <span key={s} className="text-xs rounded-full bg-secondary px-2.5 py-1 font-medium">{s}</span>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-primary/5 border border-primary/10 p-4">
              <div className="text-xs font-bold text-primary uppercase tracking-[0.15em]">Recommendation</div>
              <p className="mt-2 text-sm text-foreground/85">
                "Miguel finished our restaurant fit-out two days early with joinery that turned heads. Precise, calm and reliable — an easy re-hire."
              </p>
              <p className="mt-2 text-xs text-muted-foreground">— Sarah Chen · DataFlow Solutions</p>
            </div>
          </motion.div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Hammer className="w-4 h-4 text-primary" /> Carpentry</span>
            <span className="inline-flex items-center gap-1.5"><Cpu className="w-4 h-4 text-primary" /> Digital Tasks</span>
            <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-primary" /> Logistics</span>
          </div>
        </div>
      </main>
    </div>
  );
}

const Feature = ({ icon, title, text }) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
    <div className="mt-2.5 font-heading font-bold text-sm">{title}</div>
    <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{text}</div>
  </div>
);
